<template>
    <div class="space-y-4">
        <!-- Stats Overview -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
                title="Total Users"
                :value="data.stats.totalUsers || 0"
                :icon="Users"
                color="blue"
            />
            <StatCard
                title="Total Students"
                :value="data.stats.totalStudents || 0"
                :icon="GraduationCap"
                color="green"
            />
            <StatCard
                title="Total Teachers"
                :value="data.stats.totalTeachers || 0"
                :icon="User"
                color="purple"
            />
            <StatCard
                title="Active Academic Year"
                :value="data.stats.activeAcademicYear || 'N/A'"
                :icon="Calendar"
                color="yellow"
            />
        </div>

        <!-- Online Users Section -->
        <OnlineUsers />

        <!-- Master Data Health and User Distribution -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <!-- Master Data Health -->
            <div class="bg-white dark:bg-gray-800 shadow rounded-lg">
                <div class="px-4 py-4 sm:p-5">
                    <h3
                        class="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4"
                    >
                        Master Data Health
                    </h3>
                    <div class="space-y-4">
                        <div class="flex items-center justify-between">
                            <span
                                class="text-sm font-medium text-gray-900 dark:text-white"
                            >
                                Academic Years
                            </span>
                            <div class="flex items-center space-x-2">
                                <span
                                    class="text-sm text-gray-500 dark:text-gray-400"
                                >
                                    {{
                                        data.masterDataHealth.academicYears
                                            .active
                                    }}/{{
                                        data.masterDataHealth.academicYears
                                            .total
                                    }}
                                </span>
                                <div
                                    class="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2"
                                >
                                    <div
                                        class="bg-green-500 h-2 rounded-full"
                                        :style="{
                                            width: `${(data.masterDataHealth.academicYears.active / data.masterDataHealth.academicYears.total) * 100}%`,
                                        }"
                                    ></div>
                                </div>
                            </div>
                        </div>

                        <div class="flex items-center justify-between">
                            <span
                                class="text-sm font-medium text-gray-900 dark:text-white"
                            >
                                Semesters
                            </span>
                            <div class="flex items-center space-x-2">
                                <span
                                    class="text-sm text-gray-500 dark:text-gray-400"
                                >
                                    {{
                                        data?.masterDataHealth?.semesters
                                            ?.active
                                    }}/{{
                                        data?.masterDataHealth?.semesters?.total
                                    }}
                                </span>
                                <div
                                    class="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2"
                                >
                                    <div
                                        class="bg-green-500 h-2 rounded-full"
                                        :style="{
                                            width: `${(data.masterDataHealth.semesters.active / data.masterDataHealth.semesters.total) * 100}%`,
                                        }"
                                    ></div>
                                </div>
                            </div>
                        </div>

                        <div class="flex items-center justify-between">
                            <span
                                class="text-sm font-medium text-gray-900 dark:text-white"
                            >
                                Classes
                            </span>
                            <div class="flex items-center space-x-2">
                                <span
                                    class="text-sm text-gray-500 dark:text-gray-400"
                                >
                                    {{
                                        data.masterDataHealth.classes.active
                                    }}/{{ data.masterDataHealth.classes.total }}
                                </span>
                                <div
                                    class="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2"
                                >
                                    <div
                                        class="bg-green-500 h-2 rounded-full"
                                        :style="{
                                            width: `${(data.masterDataHealth.classes.active / data.masterDataHealth.classes.total) * 100}%`,
                                        }"
                                    ></div>
                                </div>
                            </div>
                        </div>

                        <div class="flex items-center justify-between">
                            <span
                                class="text-sm font-medium text-gray-900 dark:text-white"
                            >
                                Subjects
                            </span>
                            <div class="flex items-center space-x-2">
                                <span
                                    class="text-sm text-gray-500 dark:text-gray-400"
                                >
                                    {{
                                        data.masterDataHealth.subjects.active
                                    }}/{{
                                        data.masterDataHealth.subjects.total
                                    }}
                                </span>
                                <div
                                    class="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2"
                                >
                                    <div
                                        class="bg-green-500 h-2 rounded-full"
                                        :style="{
                                            width: `${(data.masterDataHealth.subjects.active / data.masterDataHealth.subjects.total) * 100}%`,
                                        }"
                                    ></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- User Distribution Chart -->
            <UserDistributionChart :distribution="data.userDistribution" />
        </div>

        <!-- Quick Actions -->
        <QuickActions :actions="data.quickActions" />
    </div>
</template>

<script setup lang="ts">
import { SuperAdminDashboardData } from "@/types/dashboard";
import StatCard from "./StatCard.vue";
import QuickActions from "./QuickActions.vue";
import UserDistributionChart from "./UserDistributionChart.vue";
import OnlineUsers from "./OnlineUsers.vue";
import { Users, GraduationCap, User, Calendar } from "@lucide/vue";

interface Props {
    data: SuperAdminDashboardData;
}

defineProps<Props>();
</script>
